<template>
  <div class="bg-white rounded-lg shadow-md p-6">
    <!-- Access Denied for Non-Admins -->
    <div v-if="!isAdmin" class="flex flex-col items-center justify-center py-12">
      <div class="bg-red-50 rounded-full p-6 mb-4">
        <svg class="h-16 w-16 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
        </svg>
      </div>
      <h3 class="text-xl font-medium mb-2 text-center">Access Denied</h3>
      <p class="text-gray-500 mb-6 text-center max-w-md">
        You need Administrator privileges to access User Role Management.
        Please contact your system administrator if you believe you should have access to this feature.
      </p>
      <router-link
        to="/administration"
        class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 inline-block"
      >
        Return to Administration
      </router-link>
    </div>

    <!-- Admin Content -->
    <div v-else>
      <div class="mb-6">
        <h3 class="text-xl font-medium mb-2">User Role Management</h3>
        <p class="text-gray-500 mb-4">
          Manage user roles and permissions across the system. You can edit user business roles, system roles, and report types.
        </p>
      </div>

    <!-- User Profiles Table -->
    <div>
      <div ref="tableContainer" class="overflow-auto" style="max-height: 400px; border: 1px solid #e5e7eb; border-radius: 0.375rem;">
        <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50 sticky top-0 z-10">
          <tr>
            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              User
              <span class="text-xs font-normal text-gray-400 ml-2 lowercase">
                ({{ profiles.length }} users)
              </span>
            </th>
            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Business Role</th>
            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">System Role</th>
            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Report Type</th>
            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr v-if="loading">
            <td colspan="5" class="px-6 py-4 text-center text-gray-500">
              Loading user profiles...
            </td>
          </tr>
          <tr v-else-if="profiles.length === 0">
            <td colspan="5" class="px-6 py-4 text-center text-gray-500">
              No user profiles found.
            </td>
          </tr>
          <tr v-for="(profile, index) in profiles" :key="index" class="hover:bg-gray-50 transition-colors duration-300" :data-id="profile.id">
            <td class="px-6 py-4 whitespace-nowrap">
              <div class="flex items-center">
                <div class="flex-shrink-0 h-10 w-10">
                  <img v-if="profile.avatar_url" class="h-10 w-10 rounded-full object-cover" :src="profile.avatar_url" :alt="profile.name" />
                  <div v-else class="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                    <span class="text-sm font-medium text-gray-700">{{ getInitials(profile.name) }}</span>
                  </div>
                </div>
                <div class="ml-4">
                  <div class="text-sm font-medium text-gray-900">{{ profile.name || 'No name' }}</div>
                  <div class="text-sm text-gray-500">{{ profile.email || 'No email' }}</div>
                </div>
              </div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                    :class="getBusinessRoleBadgeClass(profile.business_role)">
                {{ formatBusinessRole(profile.business_role) }}
              </span>
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                    :class="getSystemRoleBadgeClass(profile.system_role)">
                {{ formatSystemRole(profile.system_role) }}
              </span>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {{ profile.report_type || 'Not assigned' }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
              <button @click="editProfile(profile)" class="text-indigo-600 hover:text-indigo-900">Edit</button>
            </td>
          </tr>
        </tbody>
        </table>
      </div>
    </div>

    <!-- Edit Profile Form -->
    <div v-if="isEditing" class="mt-8 bg-gray-50 p-6 rounded-md">
      <h4 class="text-lg font-medium mb-4">Edit User Profile</h4>
      <form @submit.prevent="saveProfile">
        <!-- User Info Display -->
        <div v-if="editingProfile" class="mb-6 p-4 bg-white rounded-md border">
          <div class="flex items-center">
            <div class="flex-shrink-0 h-12 w-12">
              <img v-if="editingProfile.avatar_url" class="h-12 w-12 rounded-full object-cover" :src="editingProfile.avatar_url" :alt="editingProfile.name" />
              <div v-else class="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center">
                <span class="text-lg font-medium text-gray-700">{{ getInitials(editingProfile.name) }}</span>
              </div>
            </div>
            <div class="ml-4">
              <div class="text-lg font-medium text-gray-900">{{ editingProfile.name || 'No name' }}</div>
              <div class="text-sm text-gray-500">{{ editingProfile.email || 'No email' }}</div>
            </div>
          </div>
        </div>

        <!-- Role Configuration -->
        <div class="mb-6">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <!-- Business Role Field -->
            <div>
              <label for="business_role" class="block text-sm font-medium text-gray-700">
                Business Role <span class="text-red-500">*</span>
              </label>
              <select
                id="business_role"
                v-model="formData.business_role"
                class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                required
                :class="{'border-red-300 focus:ring-red-500 focus:border-red-500': !formData.business_role && formTouched}"
              >
                <option value="">Select a business role</option>
                <option v-for="role in businessRoleOptions" :key="role" :value="role">
                  {{ formatBusinessRole(role) }}
                </option>
              </select>
              <p v-if="!formData.business_role && formTouched" class="mt-1 text-sm text-red-500">
                Business Role is required
              </p>
            </div>
            
            <!-- System Role Field -->
            <div>
              <label for="system_role" class="block text-sm font-medium text-gray-700">
                System Role <span class="text-red-500">*</span>
              </label>
              <select
                id="system_role"
                v-model="formData.system_role"
                class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                required
                :class="{'border-red-300 focus:ring-red-500 focus:border-red-500': !formData.system_role && formTouched}"
              >
                <option value="">Select a system role</option>
                <option v-for="role in systemRoleOptions" :key="role" :value="role">
                  {{ formatSystemRole(role) }}
                </option>
              </select>
              <p v-if="!formData.system_role && formTouched" class="mt-1 text-sm text-red-500">
                System Role is required
              </p>
            </div>
            
            <!-- Report Type Field -->
            <div>
              <label for="report_type" class="block text-sm font-medium text-gray-700">Report Type</label>
              <select
                id="report_type"
                v-model="formData.report_type"
                class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              >
                <option value="">Not assigned</option>
                <option v-for="type in reportTypeOptions" :key="type" :value="type">{{ type }}</option>
              </select>
            </div>
          </div>
        </div>
        
        <div class="mt-6 flex justify-between items-center">
          <p class="text-sm text-gray-500">Fields marked with <span class="text-red-500">*</span> are required</p>
          
          <div class="flex space-x-3">
            <button
              type="button"
              @click="cancelEdit"
              class="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              :disabled="!isFormValid"
              :title="!isFormValid ? 'Please fill in all required fields' : ''"
            >
              Update Profile
            </button>
          </div>
        </div>
      </form>
    </div>

      <div class="mt-8 flex justify-center">
        <router-link
          to="/administration"
          class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 inline-block"
        >
          Return to Administration
        </router-link>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed, watch } from 'vue';
import { supabase } from '@/services/supabase';
import { logger, LogCategory } from '@/services/logger';
import { useAuth } from '@/composables/useAuth';
import { BusinessRole, formatBusinessRole } from '@/types/businessRole';
import { SystemRole, formatSystemRole } from '@/types/systemRole';
import { REPORT_TYPE } from '@/types/document';

interface UserProfile {
  id: string;
  name: string;
  email: string;
  avatar_url?: string;
  business_role: BusinessRole;
  system_role: SystemRole;
  report_type?: REPORT_TYPE;
}

export default defineComponent({
  name: 'UserRoleManagement',
  emits: ['navigate'],
  setup() {
    // Auth composable
    const { profile: currentUserProfile, init } = useAuth();
    
    // Check if current user is admin
    const isAdmin = computed(() => {
      return currentUserProfile.value?.system_role === SystemRole.ADMIN;
    });
    
    const profiles = ref<UserProfile[]>([]);
    const loading = ref(true);
    const isEditing = ref(false);
    const editingProfile = ref<UserProfile | null>(null);
    const tableContainer = ref<HTMLElement | null>(null);
    
    const formData = ref({
      business_role: '',
      system_role: '',
      report_type: ''
    });
    
    // Enum options
    const businessRoleOptions = Object.values(BusinessRole);
    const systemRoleOptions = Object.values(SystemRole);
    const reportTypeOptions = Object.values(REPORT_TYPE);
    
    // Track if form has been touched
    const formTouched = ref(false);
    
    // Watch for any changes to formData to mark form as touched
    watch(() => formData.value, () => {
      formTouched.value = true;
    }, { deep: true });
    
    
    // Computed property to check if form is valid
    const isFormValid = computed(() => {
      if (!formTouched.value) {
        return false;
      }
      
      return !!(formData.value.business_role && formData.value.system_role);
    });
    
    // Fetch all user profiles from the database
    const fetchProfiles = async () => {
      loading.value = true;
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('id, name, email, avatar_url, business_role, system_role, report_type')
          .order('name');
          
        if (error) {
          throw error;
        }
        
        profiles.value = data || [];
        logger.info('User profiles fetched successfully', LogCategory.DATABASE, {
          count: profiles.value.length
        });
      } catch (error) {
        logger.error('Error fetching user profiles', LogCategory.DATABASE, { error });
      } finally {
        loading.value = false;
      }
    };
    
    // Edit a user profile
    const editProfile = (profile: UserProfile) => {
      isEditing.value = true;
      editingProfile.value = { ...profile };
      formTouched.value = true;
      
      formData.value = {
        business_role: profile.business_role || '',
        system_role: profile.system_role || '',
        report_type: profile.report_type || ''
      };
    };
    
    // Save profile changes
    const saveProfile = async () => {
      if (!editingProfile.value || !isFormValid.value) {
        return;
      }
      
      try {
        const payload = {
          business_role: formData.value.business_role,
          system_role: formData.value.system_role,
          report_type: formData.value.report_type || null
        };
        
        const { error } = await supabase
          .from('profiles')
          .update(payload)
          .eq('id', editingProfile.value.id);
          
        if (error) throw error;
        
        logger.info('User profile updated successfully', LogCategory.DATABASE, {
          userId: editingProfile.value.id,
          ...payload
        });
        
        // Show success message
        const successMessage = document.createElement('div');
        successMessage.className = 'fixed bottom-4 right-4 bg-green-500 text-white px-4 py-2 rounded shadow-lg z-50';
        successMessage.textContent = `Profile for "${editingProfile.value.name}" updated successfully!`;
        document.body.appendChild(successMessage);
        
        setTimeout(() => {
          document.body.removeChild(successMessage);
        }, 3000);
        
        // Reset form and refresh data
        cancelEdit();
        await fetchProfiles();
      } catch (error) {
        logger.error('Error updating user profile', LogCategory.DATABASE, { error });
        alert('Failed to update user profile. Please try again.');
      }
    };
    
    // Cancel editing
    const cancelEdit = () => {
      isEditing.value = false;
      editingProfile.value = null;
      formTouched.value = false;
      formData.value = {
        business_role: '',
        system_role: '',
        report_type: ''
      };
    };
    
    // Get user initials for avatar placeholder
    const getInitials = (name: string): string => {
      if (!name) return '?';
      
      const names = name.trim().split(' ');
      if (names.length === 1) {
        return names[0].charAt(0).toUpperCase();
      }
      
      return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
    };
    
    // Get badge class for business role
    const getBusinessRoleBadgeClass = (role: BusinessRole): string => {
      switch (role) {
        case BusinessRole.HEAD_OF_PCO:
          return 'bg-purple-100 text-purple-800';
        case BusinessRole.CONTROLLER:
          return 'bg-blue-100 text-blue-800';
        case BusinessRole.PLANNER:
          return 'bg-green-100 text-green-800';
        default:
          return 'bg-gray-100 text-gray-800';
      }
    };
    
    // Get badge class for system role
    const getSystemRoleBadgeClass = (role: SystemRole): string => {
      switch (role) {
        case SystemRole.ADMIN:
          return 'bg-red-100 text-red-800';
        case SystemRole.USER:
          return 'bg-gray-100 text-gray-800';
        default:
          return 'bg-gray-100 text-gray-800';
      }
    };
    
    // Fetch profiles on component mount
    onMounted(async () => {
      await init(); // Initialize auth to get current user profile
      fetchProfiles();
    });
    
    return {
      isAdmin,
      profiles,
      loading,
      isEditing,
      editingProfile,
      formData,
      businessRoleOptions,
      systemRoleOptions,
      reportTypeOptions,
      formTouched,
      isFormValid,
      tableContainer,
      editProfile,
      saveProfile,
      cancelEdit,
      getInitials,
      getBusinessRoleBadgeClass,
      getSystemRoleBadgeClass,
      formatBusinessRole,
      formatSystemRole
    };
  }
});
</script>

<style scoped>
.bg-green-50 {
  background-color: rgba(240, 253, 244, 1);
  transition: background-color 0.5s ease-in-out;
}
</style>